/**
 * One checkpoint, created or edited.
 *
 * It watches the whole collection rather than a single document, on purpose:
 * `validateCourse` needs every checkpoint to see that two share a route
 * position or a number. Validating the course with the draft substituted means
 * those clashes appear while typing, rather than after saving and reading the
 * list.
 *
 * Open and close times are deliberately absent — see the note at the bottom.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  DWELL_PRESET_MINUTES,
  errors,
  isStorable,
  isUsableDocumentId,
  suggestCheckpointId,
  validateCheckpoint,
  validateCourse,
  warnings,
  type Checkpoint,
  type CheckpointId,
  type Facilities,
} from '@cairn/domain';

import {
  createCheckpoint,
  updateCheckpoint,
  watchCheckpoints,
} from './data/checkpoints.js';
import type { Membership } from './data/members.js';
import { ProblemList } from './ProblemList.js';

type SaveState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'saving' }
  | { readonly kind: 'failed'; readonly message: string };

const FACILITY_LABELS: readonly (readonly [keyof Facilities, string])[] = [
  ['bed', 'Beds'],
  ['water', 'Water'],
  ['food', 'Food'],
  ['medic', 'Medic'],
  ['mechanical', 'Mechanical'],
  ['bagDrop', 'Bag drop'],
  ['dryRoom', 'Dry room'],
  ['toilets', 'Toilets'],
];

function blankCheckpoint(routeOrder: number): Checkpoint {
  return {
    id: '' as CheckpointId,
    number: routeOrder,
    name: '',
    role: 'intermediate',
    routeOrder,
    physicalSiteId: '',
    // D43's sibling: null means inherit, and it is the honest starting point.
    maxDwellMinutes: null,
    staffingModel: 'staffed',
    isSleepStop: false,
    facilities: {
      bed: false,
      water: false,
      food: false,
      medic: false,
      mechanical: false,
      bagDrop: false,
      dryRoom: false,
      toilets: false,
    },
    // Null rather than empty strings: a plausible-looking wrong location is
    // worse than none when ambulance control is the consumer.
    location: null,
    connectivity: 'cell',
  };
}

export function CheckpointEditor({
  membership,
  mode,
}: {
  readonly membership: Membership;
  readonly mode: 'create' | 'edit';
}): React.JSX.Element {
  const { checkpointId } = useParams();
  const navigate = useNavigate();

  const [all, setAll] = useState<readonly Checkpoint[] | null>(null);
  const [draft, setDraft] = useState<Checkpoint | null>(null);
  const [idDraft, setIdDraft] = useState('');
  const [idEdited, setIdEdited] = useState(false);
  const [save, setSave] = useState<SaveState>({ kind: 'idle' });
  const [changedElsewhere, setChangedElsewhere] = useState(false);

  const editing = useRef(false);
  const lastFromServer = useRef<string | null>(null);

  const canEdit = membership.role === 'organiser';

  useEffect(() => {
    let cancelled = false;
    let unsubscribe: (() => void) | undefined;

    void (async () => {
      try {
        const stop = await watchCheckpoints(membership.eventId, (list) => {
          if (cancelled) return;
          setAll(list);

          if (mode === 'create') {
            setDraft((current) => current ?? blankCheckpoint(list.length));
            return;
          }

          const mine = list.find((c) => c.id === checkpointId) ?? null;
          const signature = mine === null ? null : JSON.stringify(mine);

          // Same rule as the event screen, and for the same reason: onSnapshot
          // fires from the local cache first, so seeding once would pin the
          // form to a stale copy and save it back over someone else's change.
          if (!editing.current) {
            setDraft(mine);
            setChangedElsewhere(false);
          } else if (lastFromServer.current !== null && signature !== lastFromServer.current) {
            setChangedElsewhere(true);
          }
          lastFromServer.current = signature;
        });

        if (cancelled) stop();
        else unsubscribe = stop;
      } catch (error: unknown) {
        if (!cancelled) {
          setSave({ kind: 'failed', message: messageOf(error) });
          setAll([]);
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [membership.eventId, checkpointId, mode]);

  const onSave = useCallback(() => {
    if (draft === null) return;
    setSave({ kind: 'saving' });

    void (async () => {
      try {
        if (mode === 'create') {
          await createCheckpoint(membership.eventId, { ...draft, id: idDraft as CheckpointId });
        } else {
          const { id: _id, ...edits } = draft;
          await updateCheckpoint(membership.eventId, draft.id, edits);
        }
        editing.current = false;
        setChangedElsewhere(false);
        void navigate('/checkpoints');
      } catch (error: unknown) {
        setSave({ kind: 'failed', message: messageOf(error) });
      }
    })();
  }, [draft, idDraft, membership.eventId, mode, navigate]);

  if (all === null) return <p>Loading…</p>;

  if (draft === null) {
    return (
      <section>
        <h2>Checkpoint</h2>
        <p role="alert">
          There is no checkpoint with the id {checkpointId ?? '(none given)'}. It may have been
          deleted.
        </p>
      </section>
    );
  }

  const set = <K extends keyof Checkpoint>(key: K, value: Checkpoint[K]): void => {
    editing.current = true;
    const next = { ...draft, [key]: value };
    setDraft(next);
    setSave({ kind: 'idle' });

    // The id follows the name and number until it is edited by hand, and is
    // fixed for ever once the checkpoint exists — it is the document path.
    if (mode === 'create' && !idEdited && (key === 'name' || key === 'number')) {
      setIdDraft(suggestCheckpointId(next.number, next.name));
    }
  };

  const setFacility = (key: keyof Facilities, value: boolean): void => {
    set('facilities', { ...draft.facilities, [key]: value });
  };


  // The course as it would be with this draft in it, so a clash appears while
  // it is being typed rather than after it has been saved.
  //
  // What counts as "the others" differs by mode, and getting it wrong hides
  // the very thing this is for. Editing replaces the stored copy of *this*
  // checkpoint with the draft. Creating adds to all of them — filtering by the
  // draft's id there would remove an existing checkpoint that already holds
  // it, which is precisely the collision worth reporting.
  const candidateId = (mode === 'create' ? idDraft : draft.id) as CheckpointId;
  const own = validateCheckpoint({ ...draft, id: candidateId });
  const others = mode === 'create' ? all : all.filter((c) => c.id !== draft.id);
  const course = validateCourse([...others, { ...draft, id: candidateId }]);

  const idProblem =
    mode === 'create' && !isUsableDocumentId(idDraft)
      ? 'The id must be lowercase letters, numbers and hyphens, 2–63 characters.'
      : null;

  const blocked = !isStorable(own) || !isStorable(course) || idProblem !== null;

  return (
    <section>
      <h2>{mode === 'create' ? 'Add a checkpoint' : draft.name || 'Checkpoint'}</h2>

      {!canEdit && <p>You are signed in as {membership.role}. This is read-only.</p>}

      {changedElsewhere && (
        <p role="alert">Someone else changed this checkpoint while you were editing.</p>
      )}

      <label>
        Name
        <input
          type="text"
          value={draft.name}
          disabled={!canEdit}
          onChange={(e) => { set('name', e.target.value); }}
        />
      </label>

      {mode === 'create' && (
        <label>
          Id
          <input
            type="text"
            value={idDraft}
            disabled={!canEdit}
            onChange={(e) => {
              setIdEdited(true);
              setIdDraft(e.target.value);
            }}
          />
          <span> Appears in the address bar. It cannot be changed later.</span>
        </label>
      )}

      <label>
        Number
        <input
          type="number"
          value={String(draft.number)}
          disabled={!canEdit}
          onChange={(e) => { set('number', Number(e.target.value)); }}
        />
      </label>

      {/* Separate from number, because a figure-of-8 reuses a site under a
          different number and routeOrder is what must stay strictly linear. */}
      <label>
        Route position
        <input
          type="number"
          value={String(draft.routeOrder)}
          disabled={!canEdit}
          onChange={(e) => { set('routeOrder', Number(e.target.value)); }}
        />
      </label>

      <label>
        Role
        <select
          value={draft.role}
          disabled={!canEdit}
          onChange={(e) => { set('role', e.target.value as Checkpoint['role']); }}
        >
          <option value="start">Start</option>
          <option value="intermediate">Intermediate</option>
          <option value="finish">Finish</option>
        </select>
      </label>

      <label>
        Physical site
        <input
          type="text"
          value={draft.physicalSiteId}
          disabled={!canEdit}
          onChange={(e) => { set('physicalSiteId', e.target.value); }}
        />
        <span> Groups checkpoints that share one site on a figure-of-8 course.</span>
      </label>

      <label>
        Staffing
        <select
          value={draft.staffingModel}
          disabled={!canEdit}
          onChange={(e) => { set('staffingModel', e.target.value as Checkpoint['staffingModel']); }}
        >
          <option value="staffed">Staffed — continuously crewed</option>
          <option value="intermittent">Intermittent — crewed at peak only</option>
          <option value="unattended">Unattended — never alert</option>
        </select>
      </label>

      <label>
        <input
          type="checkbox"
          checked={draft.isSleepStop}
          disabled={!canEdit}
          onChange={(e) => { set('isSleepStop', e.target.checked); }}
        />
        Sleep stop — long dwell is expected and normal here
      </label>

      <label>
        Maximum dwell
        <select
          value={draft.maxDwellMinutes === null ? 'inherit' : String(draft.maxDwellMinutes)}
          disabled={!canEdit}
          onChange={(e) => {
            set('maxDwellMinutes', e.target.value === 'inherit' ? null : Number(e.target.value));
          }}
        >
          <option value="inherit">Use the event default</option>
          {DWELL_PRESET_MINUTES.map((minutes) => (
            <option key={minutes} value={minutes}>
              {describeMinutes(minutes)}
            </option>
          ))}
        </select>
      </label>

      <label>
        Signal
        <select
          value={draft.connectivity}
          disabled={!canEdit}
          onChange={(e) => { set('connectivity', e.target.value as Checkpoint['connectivity']); }}
        >
          <option value="cell">Cell</option>
          <option value="wifi">WiFi only</option>
          <option value="both">Cell and WiFi</option>
        </select>
      </label>

      <fieldset>
        <legend>Facilities</legend>
        {FACILITY_LABELS.map(([key, label]) => (
          <label key={key}>
            <input
              type="checkbox"
              checked={draft.facilities[key]}
              disabled={!canEdit}
              onChange={(e) => { setFacility(key, e.target.checked); }}
            />
            {label}
          </label>
        ))}
      </fieldset>

      <fieldset>
        <legend>Location</legend>
        <label>
          what3words
          <input
            type="text"
            value={draft.location?.what3words ?? ''}
            disabled={!canEdit}
            onChange={(e) => {
              set('location', withLocation(draft, { what3words: emptyToNull(e.target.value) }));
            }}
          />
        </label>
        <label>
          Latitude
          <input
            type="number"
            value={draft.location?.latitude === null || draft.location === null ? '' : String(draft.location.latitude)}
            disabled={!canEdit}
            onChange={(e) => {
              set('location', withLocation(draft, { latitude: numberOrNull(e.target.value) }));
            }}
          />
        </label>
        <label>
          Longitude
          <input
            type="number"
            value={draft.location?.longitude === null || draft.location === null ? '' : String(draft.location.longitude)}
            disabled={!canEdit}
            onChange={(e) => {
              set('location', withLocation(draft, { longitude: numberOrNull(e.target.value) }));
            }}
          />
        </label>
      </fieldset>

      {idProblem !== null && (
        <div role="alert">
          <h3>Fix before saving</h3>
          <ul>
            <li>{idProblem}</li>
          </ul>
        </div>
      )}

      <ProblemList problems={errors(own)} heading="Fix before saving" role="alert" />
      <ProblemList problems={errors(course)} heading="Problems with the course" role="alert" />
      <ProblemList problems={[...warnings(own), ...warnings(course)]} heading="Worth checking" />

      {canEdit && (
        <p>
          <button type="button" onClick={onSave} disabled={blocked || save.kind === 'saving'}>
            {save.kind === 'saving' ? 'Saving…' : 'Save'}
          </button>
          {save.kind === 'failed' && <span role="alert"> {save.message}</span>}
        </p>
      )}
    </section>
  );
}

/**
 * Editing one part of a location when there is no location yet has to create
 * one, and clearing the last part has to remove it again.
 *
 * Null means "not surveyed", and that is the only state `validateCheckpoint`
 * warns about. A what3words on its own therefore counts as a location and
 * clears the warning, which is right — it is what crews and ambulance control
 * actually use, and waiting for a grid reference too would leave the warning
 * up on a checkpoint that is perfectly well located.
 */
function withLocation(
  draft: Checkpoint,
  patch: Partial<NonNullable<Checkpoint['location']>>,
): Checkpoint['location'] {
  const current = draft.location ?? { what3words: null, latitude: null, longitude: null };
  const next = { ...current, ...patch };

  return next.what3words === null && next.latitude === null && next.longitude === null
    ? null
    : next;
}

const emptyToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

const numberOrNull = (value: string): number | null =>
  value.trim() === '' || Number.isNaN(Number(value)) ? null : Number(value);

function describeMinutes(minutes: number): string {
  if (minutes < 60) return `${String(minutes)} minutes`;
  const hours = minutes / 60;
  return hours === 1 ? '1 hour' : `${String(hours)} hours`;
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
